import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import Alert from '../components/Alert';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../context/AuthContext';
import { formatPatientNameForDisplay } from '../utils/patientNameUtils';
import { generateInternalPdf, generateOrderPdf } from '../utils/pdfGenerator';

const PATIENTS_PER_PAGE = 20;

const Patients = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { isSubscriptionActive, user } = useAuth();
    const [patients, setPatients] = useState([]);
    const [totalPatients, setTotalPatients] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [measuredByOptions, setMeasuredByOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [sortField, setSortField] = useState('updatedAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [measuredByFilter, setMeasuredByFilter] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeActionsMenuId, setActiveActionsMenuId] = useState(null);
    const [actionsMenuDirection, setActionsMenuDirection] = useState('down');
    const [swappingPatientId, setSwappingPatientId] = useState(null);

    const formatMeasuredByName = (measurement) => {
        if (!measurement?.userId) return 'Unknown';

        return `${measurement.userId.firstName || ''} ${measurement.userId.lastName || ''}`.trim() || 'Unknown';
    };

    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null
    });

    // Measurement history dialog states
    const [showMeasurementDialog, setShowMeasurementDialog] = useState(false);
    const [selectedPatientMeasurements, setSelectedPatientMeasurements] = useState([]);
    const [dialogLoading, setDialogLoading] = useState(false);
    const [measurementSortOrder, setMeasurementSortOrder] = useState('desc');
    const [selectedMeasurementsToDelete, setSelectedMeasurementsToDelete] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);

    // Measurements evolution dialog states
    const [showEvolutionDialog, setShowEvolutionDialog] = useState(false);
    const [evolutionPatientId, setEvolutionPatientId] = useState(null);
    const [evolutionPatientName, setEvolutionPatientName] = useState('');

    // Ref for measurement dialog scroll position
    const measurementDialogScrollRef = useRef(null);

    useEffect(() => {
        if (!activeActionsMenuId) return;

        const closeMenu = () => {
            setActiveActionsMenuId(null);
            setActionsMenuDirection('down');
        };
        document.addEventListener('click', closeMenu);

        return () => {
            document.removeEventListener('click', closeMenu);
        };
    }, [activeActionsMenuId]);

    const toggleActionsMenu = (patientId, patientIndex, event) => {
        event.stopPropagation();

        const triggerRect = event.currentTarget?.getBoundingClientRect();
        const estimatedMenuHeight = 180;
        const viewportPadding = 16;
        const spaceBelow = triggerRect ? window.innerHeight - triggerRect.bottom : Number.MAX_SAFE_INTEGER;
        const isTwoRowList = patients.length === 2;
        const isShortList = patients.length <= 2;
        const isNearBottom = isTwoRowList ? patientIndex === 1 : !isShortList && patientIndex >= patients.length - 2;
        const shouldOpenUp = isNearBottom || spaceBelow < (estimatedMenuHeight + viewportPadding);

        setActiveActionsMenuId((current) => {
            if (current === patientId) {
                setActionsMenuDirection('down');
                return null;
            }

            setActionsMenuDirection(shouldOpenUp ? 'up' : 'down');
            return patientId;
        });
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [appliedSearchTerm, measuredByFilter, sortField, sortOrder]);

    const fetchPatients = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/measurements/patients', {
                params: {
                    page: currentPage,
                    limit: PATIENTS_PER_PAGE,
                    search: appliedSearchTerm || undefined,
                    measuredBy: measuredByFilter || undefined,
                    sortField,
                    sortOrder,
                },
            });

            const payload = response.data || {};
            const pagination = payload.pagination || {};

            setPatients(payload.patients || []);
            setTotalPatients(pagination.total || 0);
            setTotalPages(pagination.totalPages || 1);
            setMeasuredByOptions(payload.filters?.measuredByOptions || []);

            if (pagination.page && pagination.page !== currentPage) {
                setCurrentPage(pagination.page);
            }
        } catch (error) {
            console.error('Error fetching patients:', error);
            setError(t('patients.errors.loadFailed'));
            setPatients([]);
            setTotalPatients(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    }, [appliedSearchTerm, currentPage, measuredByFilter, sortField, sortOrder, t]);

    const handleApplySearch = () => {
        setAppliedSearchTerm(searchTerm.trim());
        setCurrentPage(1);
    };

    useEffect(() => {
        fetchPatients();
    }, [fetchPatients]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
    };

    const goToPreviousPage = () => {
        setCurrentPage((prev) => Math.max(1, prev - 1));
    };

    const goToNextPage = () => {
        setCurrentPage((prev) => Math.min(totalPages, prev + 1));
    };

    const handlePatientClick = (patient) => {
        // Navigate to patient-info to view/edit patient details
        const latestMeasurement = patient.latestMeasurement;

        // Determine completed steps based on measurement data
        const steps = [];
        console.log('PatientClick - latestMeasurement:', {
            hasMeasurements: latestMeasurement.measurements && Object.keys(latestMeasurement.measurements).length > 0,
            hasSelectedProduct: !!latestMeasurement.selectedProduct,
            isOrdered: !!latestMeasurement.ordered,
            measurements: latestMeasurement.measurements,
            selectedProduct: latestMeasurement.selectedProduct,
            ordered: latestMeasurement.ordered
        });
        if (latestMeasurement.measurements && Object.keys(latestMeasurement.measurements).length > 0) {
            steps.push('measurements');
        }
        if (latestMeasurement.selectedProduct) {
            steps.push('productSelection');
        }
        if (latestMeasurement.ordered) {
            steps.push('order');
        }
        console.log('PatientClick - completedSteps:', steps);

        navigate('/patient-info', {
            state: {
                measurementId: latestMeasurement._id,
                patientId: patient.patientId,
                patientFirstName: patient.patientFirstName,
                patientLastName: patient.patientLastName,
                firstName: patient.patientFirstName,
                lastName: patient.patientLastName,
                dateOfBirth: latestMeasurement.patientDateOfBirth || '',
                gender: latestMeasurement.patientGender || '',
                address: latestMeasurement.patientAddress || '',
                phoneNumber: latestMeasurement.patientPhoneNumber || '',
                email: latestMeasurement.patientEmail || '',
                notes: latestMeasurement.notes || '',
                isReadOnly: true,
                allowPatientEdit: true,
                allowNotesEdit: true,
                completedSteps: steps,
                ordered: latestMeasurement.ordered || false,
                selectedProduct: latestMeasurement.selectedProduct || null,
                hideMeasurementsNavigation: true,
            }
        });
    };

    const handleViewLatestMeasurement = (patient, e) => {
        e.stopPropagation();
        // Clear previous patient's data first to prevent showing stale data
        setSelectedPatientMeasurements([]);
        // Show measurement history dialog instead of navigating directly
        setShowMeasurementDialog(true);
        fetchPatientMeasurementsData(patient.patientId);
    };

    const handleShowEvolution = (patient, e) => {
        e.stopPropagation();
        // Clear previous patient's data first to prevent showing stale data
        setSelectedPatientMeasurements([]);
        setEvolutionPatientId(patient.patientId);
        setEvolutionPatientName(formatPatientNameForDisplay(patient.patientFirstName, patient.patientLastName));
        setShowEvolutionDialog(true);
        fetchPatientMeasurementsData(patient.patientId);
    };

    const fetchPatientMeasurementsData = async (patientId) => {
        try {
            setDialogLoading(true);
            setSelectedMeasurementsToDelete([]); // Reset selection when opening dialog
            const response = await axios.get('/api/measurements', { params: { patientId } });
            const allMeasurements = response.data;

            setSelectedPatientMeasurements(allMeasurements);
        } catch (error) {
            console.error('Error fetching patient measurements:', error);
            setError(t('patients.errors.loadMeasurementsFailed'));
        } finally {
            setDialogLoading(false);
        }
    };

    const handleSelectMeasurement = (measurement, isEditable = false) => {
        // Navigate to leg-measuring in read-only mode
        navigate('/leg-measuring', {
            state: {
                measurementId: measurement._id,
                patientId: measurement.patientId,
                patientFirstName: measurement.patientFirstName,
                patientLastName: measurement.patientLastName,
                patientDateOfBirth: measurement.patientDateOfBirth,
                patientGender: measurement.patientGender,
                patientAddress: measurement.patientAddress,
                patientPhoneNumber: measurement.patientPhoneNumber,
                patientEmail: measurement.patientEmail,
                notes: measurement.notes,
                measurements: measurement.measurements,
                measurements2: measurement.measurements2,
                compressionClass: measurement.compressionClass,
                lengthType: measurement.lengthType,
                knitting: measurement.knitting,
                allowNotesEdit: true,
                fromPatients: true,
                isReadOnly: !isEditable,
            }
        });
        setShowMeasurementDialog(false);
    };

    const getSortedMeasurements = () => {
        const sorted = [...selectedPatientMeasurements];
        sorted.sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return measurementSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        });
        return sorted;
    };

    const handleToggleMeasurementSelection = (measurementId) => {
        // Preserve scroll position
        const scrollTop = measurementDialogScrollRef.current?.scrollTop;

        setSelectedMeasurementsToDelete(prev => {
            if (prev.includes(measurementId)) {
                return prev.filter(id => id !== measurementId);
            } else {
                return [...prev, measurementId];
            }
        });

        // Restore scroll position after state update
        if (scrollTop !== undefined) {
            requestAnimationFrame(() => {
                if (measurementDialogScrollRef.current) {
                    measurementDialogScrollRef.current.scrollTop = scrollTop;
                }
            });
        }
    };

    const handleToggleAllMeasurements = () => {
        // Preserve scroll position
        const scrollTop = measurementDialogScrollRef.current?.scrollTop;

        if (selectedMeasurementsToDelete.length === selectedPatientMeasurements.length) {
            setSelectedMeasurementsToDelete([]);
        } else {
            setSelectedMeasurementsToDelete(selectedPatientMeasurements.map(m => m._id));
        }

        // Restore scroll position after state update
        if (scrollTop !== undefined) {
            requestAnimationFrame(() => {
                if (measurementDialogScrollRef.current) {
                    measurementDialogScrollRef.current.scrollTop = scrollTop;
                }
            });
        }
    };

    const handleDeleteMeasurements = async () => {
        if (selectedMeasurementsToDelete.length === 0) return;

        const count = selectedMeasurementsToDelete.length;
        const proceed = await new Promise((resolve) => {
            setConfirmDialog({
                isOpen: true,
                title: t('confirmDelete.title'),
                message: t('confirmDelete.measurements', {
                    count,
                    plural: count > 1 ? 's' : ''
                }),
                onConfirm: () => {
                    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
                    resolve(true);
                },
                onCancel: () => {
                    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
                    resolve(false);
                }
            });
        });

        if (!proceed) return;

        try {
            setIsDeleting(true);

            // Delete measurements one by one
            await Promise.all(
                selectedMeasurementsToDelete.map(id =>
                    axios.delete(`/api/measurements/${id}`)
                )
            );

            // Remove deleted measurements from the dialog
            const remainingMeasurements = selectedPatientMeasurements.filter(
                m => !selectedMeasurementsToDelete.includes(m._id)
            );
            setSelectedPatientMeasurements(remainingMeasurements);
            setSelectedMeasurementsToDelete([]);

            // Refresh the patient list
            await fetchPatients();

            setSuccess(t('patients.measurementHistory.deleteSuccess', { count, plural: count > 1 ? 's' : '' }));

            // Close dialog if all measurements were deleted
            if (remainingMeasurements.length === 0) {
                setShowMeasurementDialog(false);
            }
        } catch (error) {
            console.error('Error deleting measurements:', error);
            setError('Failed to delete some measurements. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleToggleOrdered = async (measurementId, isOrdered) => {
        try {
            const updateData = {
                ordered: isOrdered,
                orderedDate: isOrdered ? new Date().toISOString() : null,
            };

            await axios.put(`/api/measurements/${measurementId}`, updateData);

            // Update the local state
            setSelectedPatientMeasurements(prev =>
                prev.map(m =>
                    m._id === measurementId
                        ? { ...m, ordered: isOrdered, orderedDate: updateData.orderedDate }
                        : m
                )
            );

            // Refresh the patient list
            await fetchPatients();
        } catch (error) {
            console.error('Error updating ordered status:', error);
            setError(t('patients.errors.updateOrderedFailed'));
        }
    };

    const handleToggleClosed = async (measurementId, isClosed) => {
        try {
            const updateData = { closed: isClosed };

            await axios.put(`/api/measurements/${measurementId}`, updateData);

            // Update the local state
            setSelectedPatientMeasurements(prev =>
                prev.map(m =>
                    m._id === measurementId
                        ? { ...m, closed: isClosed }
                        : m
                )
            );

            // Refresh the patient list
            await fetchPatients();
        } catch (error) {
            console.error('Error updating closed status:', error);
            setError(t('patients.errors.updateClosedFailed'));
        }
    };

    const handleNewMeasurement = (patient, e) => {
        e.stopPropagation();

        // Extract patient details from the latest measurement
        const latestMeasurement = patient.latestMeasurement;

        // Navigate to patient-info with patient data pre-filled for new measurement
        navigate('/patient-info', {
            state: {
                patientId: patient.patientId,
                patientFirstName: patient.patientFirstName,
                patientLastName: patient.patientLastName,
                firstName: patient.patientFirstName,
                lastName: patient.patientLastName,
                dateOfBirth: latestMeasurement.patientDateOfBirth || '',
                gender: latestMeasurement.patientGender || '',
                address: latestMeasurement.patientAddress || '',
                phoneNumber: latestMeasurement.patientPhoneNumber || '',
                email: latestMeasurement.patientEmail || '',
                notes: latestMeasurement.notes || '',
                measurements: latestMeasurement.measurements || {},
                measurements2: latestMeasurement.measurements2 || {},
                measurementType: latestMeasurement.measurementType || '',
                selectedProduct: latestMeasurement.selectedProduct || null,
                selections: latestMeasurement.selections || {},
                compressionClass: latestMeasurement.compressionClass || '',
                lengthType: latestMeasurement.lengthType || '',
                knitting: latestMeasurement.knitting || '',
                isNewMeasurement: true, // Flag to indicate this is for a new measurement
            }
        });
    };

    const handleSwapPatientNames = async (patient, e) => {
        e.stopPropagation();
        setActiveActionsMenuId(null);

        if (!patient.patientFirstName && !patient.patientLastName) {
            setError(t('patientInfo.errors.noNamesToSwap'));
            return;
        }

        try {
            setSwappingPatientId(patient.patientId);
            setError('');
            setSuccess('');

            const response = await axios.get('/api/measurements', {
                params: { patientId: patient.patientId },
            });
            const patientMeasurements = response.data;

            if (patientMeasurements.length === 0) {
                setError(t('patientInfo.errors.noMeasurementsFound'));
                return;
            }

            const swappedFirstName = patient.patientLastName || '';
            const swappedLastName = patient.patientFirstName || '';

            await Promise.all(
                patientMeasurements.map((measurement) =>
                    axios.put(`/api/measurements/${measurement._id}`, {
                        patientFirstName: swappedFirstName,
                        patientLastName: swappedLastName,
                    })
                )
            );

            await fetchPatients();
            setSuccess(t('patientInfo.success.namesSwapped'));
        } catch (swapError) {
            console.error('Error swapping patient names:', swapError);
            setError(t('patientInfo.errors.swapNamesFailed'));
        } finally {
            setSwappingPatientId(null);
        }
    };

    const handleViewLatestProduct = (patient, e) => {
        e.stopPropagation();

        const latestMeasurement = patient.latestMeasurement;

        // Only navigate if there's a selected product
        if (!latestMeasurement.selectedProduct) {
            return;
        }

        // Navigate to StockingSelection with the product pre-filled
        navigate('/stocking-selection', {
            state: {
                measurementId: latestMeasurement._id,
                patientId: patient.patientId,
                patientFirstName: patient.patientFirstName,
                patientLastName: patient.patientLastName,
                measurements: latestMeasurement.measurements,
                compressionClass: latestMeasurement.compressionClass,
                lengthType: latestMeasurement.lengthType,
                knitting: latestMeasurement.knitting,
                selectedProduct: latestMeasurement.selectedProduct,
                currentMeasurement: latestMeasurement,
                selections: latestMeasurement.selections || {},
                orderNote: latestMeasurement.order?.orderNote || '',
                ordered: latestMeasurement.ordered || false,
                isReadOnly: true, // Make this view read-only
            }
        });
    };

    const handleOrderAgain = (patient, e) => {
        e.stopPropagation();
        setActiveActionsMenuId(null);

        const latestMeasurement = patient.latestMeasurement;

        if (!latestMeasurement?.selectedProduct) {
            setError(t('patients.errors.noProductForReorder', { defaultValue: 'No product selected for this patient yet.' }));
            return;
        }

        const nowIso = new Date().toISOString();
        const patientName = latestMeasurement.patientName || `${patient.patientFirstName || ''} ${patient.patientLastName || ''}`.trim();

        // Deep-clone to ensure all nested measurement fields/objects are copied for a new order flow.
        const measurementSnapshot = JSON.parse(JSON.stringify(latestMeasurement));

        // Clone the latest measurement snapshot into navigation state for a new order flow.
        const clonedMeasurement = {
            ...measurementSnapshot,
            _id: undefined,
            id: undefined,
            createdAt: nowIso,
            updatedAt: nowIso,
            ordered: false,
            orderedDate: null,
            closed: false,
            order: {},
        };

        navigate('/order', {
            state: {
                fromOrderAgain: true,
                selectedProduct: latestMeasurement.selectedProduct,
                currentMeasurement: clonedMeasurement,
                patientId: patient.patientId,
                patientName,
                patientFirstName: patient.patientFirstName,
                patientLastName: patient.patientLastName,
                patientDateOfBirth: latestMeasurement.patientDateOfBirth || '',
                patientGender: latestMeasurement.patientGender || '',
                patientAddress: latestMeasurement.patientAddress || '',
                patientPhoneNumber: latestMeasurement.patientPhoneNumber || '',
                patientEmail: latestMeasurement.patientEmail || '',
                notes: latestMeasurement.notes || '',
                measurements: latestMeasurement.measurements || {},
                measurements2: latestMeasurement.measurements2 || {},
                compressionClass: latestMeasurement.compressionClass || '',
                lengthType: latestMeasurement.lengthType || '',
                knitting: latestMeasurement.knitting || '',
                selections: latestMeasurement.selections || {},
                isReadOnly: false,
                ordered: false,
            }
        });
    };

    const handleDownloadInternalPdf = async (measurement) => {
        try {
            await generateInternalPdf(measurement, t);
        } catch (error) {
            console.error('Error generating Internal PDF:', error);
            setError(t('patients.errors.pdfFailed'));
        }
    };

    const handleDownloadOrderPdf = async (measurement) => {
        try {
            await generateOrderPdf(measurement, t);
        } catch (error) {
            console.error('Error generating Order PDF:', error);
            setError(t('patients.errors.pdfFailed'));
        }
    };

    const handleCreateOrder = (measurement) => {
        // Navigate to appropriate page based on whether product is already selected
        const hasProduct = measurement.selectedProduct;

        if (hasProduct) {
            // Navigate directly to order page with measurement data
            navigate('/order', {
                state: {
                    selectedProduct: measurement.selectedProduct,
                    currentMeasurement: measurement,
                    measurementId: measurement._id,
                    patientId: measurement.patientId,
                    patientName: `${measurement.patientFirstName || ''} ${measurement.patientLastName || ''}`.trim(),
                    patientFirstName: measurement.patientFirstName,
                    patientLastName: measurement.patientLastName,
                    patientDateOfBirth: measurement.patientDateOfBirth,
                    patientGender: measurement.patientGender,
                    patientAddress: measurement.patientAddress,
                    patientPhoneNumber: measurement.patientPhoneNumber,
                    patientEmail: measurement.patientEmail,
                    notes: measurement.notes,
                    measurements: measurement.measurements,
                    measurements2: measurement.measurements2,
                    compressionClass: measurement.compressionClass,
                    lengthType: measurement.lengthType,
                    knitting: measurement.knitting,
                    selections: measurement.selections || {},
                    isReadOnly: false,
                    ordered: false,
                }
            });
        } else {
            // Navigate to stocking-selection to choose product first
            navigate('/stocking-selection', {
                state: {
                    measurementId: measurement._id,
                    patientId: measurement.patientId,
                    patientName: `${measurement.patientFirstName || ''} ${measurement.patientLastName || ''}`.trim(),
                    patientFirstName: measurement.patientFirstName,
                    patientLastName: measurement.patientLastName,
                    patientDateOfBirth: measurement.patientDateOfBirth,
                    patientGender: measurement.patientGender,
                    patientAddress: measurement.patientAddress,
                    patientPhoneNumber: measurement.patientPhoneNumber,
                    patientEmail: measurement.patientEmail,
                    notes: measurement.notes,
                    measurements: measurement.measurements,
                    measurements2: measurement.measurements2,
                    compressionClass: measurement.compressionClass,
                    lengthType: measurement.lengthType,
                    knitting: measurement.knitting,
                    selections: measurement.selections || {},
                    currentMeasurement: measurement,
                    isReadOnly: false,
                    ordered: false,
                    fromPatients: true,
                }
            });
        }
        setShowMeasurementDialog(false);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Splits a date into separate date/time lines for compact table display
    const formatDateParts = (dateString) => {
        const date = new Date(dateString);
        return {
            datePart: date.toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            }),
            timePart: date.toLocaleTimeString(i18n.language, {
                hour: '2-digit',
                minute: '2-digit',
            }),
        };
    };

    // Shortens "Firstname Lastname" to "Firstname L." for the compact table column
    const formatMeasuredByCompact = (fullName) => {
        if (!fullName) return fullName;
        const parts = fullName.trim().split(/\s+/);
        if (parts.length < 2) return fullName;
        const firstName = parts[0];
        const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
        return `${firstName} ${lastInitial}.`;
    };

    const formatDateWithDay = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language, {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const isPatientCompleted = (patient) => {
        if (typeof patient.isCompleted === 'boolean') return patient.isCompleted;

        // Check if all measurements for this patient are either ordered or closed
        if (!patient.allMeasurements || patient.allMeasurements.length === 0) {
            return false;
        }
        return patient.allMeasurements.every(m => m.ordered === true || m.closed === true);
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) {
            return (
                <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
            );
        }
        return sortOrder === 'asc' ? (
            <svg className="w-4 h-4 ml-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
        ) : (
            <svg className="w-4 h-4 ml-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
        );
    };

    // Measurements Evolution Dialog Component
    const MeasurementsEvolutionDialog = () => {
        if (!showEvolutionDialog) return null;

        // Sort measurements in ascending order for timeline (oldest first)
        const timelineMeasurements = [...selectedPatientMeasurements].sort((a, b) =>
            new Date(a.createdAt) - new Date(b.createdAt)
        );

        // Define the measurement fields for each leg
        const rightLegFields = ['cGR', 'cFR', 'cER', 'cDR', 'cCR', 'cB1R', 'cBR'];
        const leftLegFields = ['cGL', 'cFL', 'cEL', 'cDL', 'cCL', 'cB1L', 'cBL'];
        const hipFields = ['cT', 'cH'];

        // Field labels for display
        const fieldLabels = {
            cGR: 'G', cFR: 'F', cER: 'E', cDR: 'D', cCR: 'C', cB1R: 'B1', cBR: 'B',
            cGL: 'G', cFL: 'F', cEL: 'E', cDL: 'D', cCL: 'C', cB1L: 'B1', cBL: 'B',
            cT: 'T (Waist)', cH: 'H (Hip)'
        };

        // Calculate min and max values for scaling the graph
        const getAllValues = (fields) => {
            const values = [];
            timelineMeasurements.forEach(m => {
                fields.forEach(field => {
                    const value = m.measurements?.[field];
                    if (value !== undefined && value !== null) {
                        values.push(value);
                    }
                });
            });
            return values;
        };

        const allRightValues = getAllValues(rightLegFields);
        const allLeftValues = getAllValues(leftLegFields);
        const allHipValues = getAllValues(hipFields);
        const allValues = [...allRightValues, ...allLeftValues, ...allHipValues];
        const minValue = allValues.length > 0 ? Math.min(...allValues) : 0;
        const maxValue = allValues.length > 0 ? Math.max(...allValues) : 100;
        const valueRange = maxValue - minValue || 1;

        // Color palette for different fields
        const colors = [
            '#3B82F6', // blue - G
            '#10B981', // green - F
            '#F59E0B', // amber - E
            '#EF4444', // red - D
            '#8B5CF6', // purple - C
            '#EC4899', // pink - B1
            '#06B6D4', // cyan - B
        ];

        const hipColors = [
            '#F97316', // orange - T
            '#14B8A6', // teal - H
        ];

        // SVG dimensions
        const svgWidth = Math.max(600, timelineMeasurements.length * 120);
        const svgHeight = 300;
        const padding = { top: 30, right: 30, bottom: 50, left: 50 };
        const graphWidth = svgWidth - padding.left - padding.right;
        const graphHeight = svgHeight - padding.top - padding.bottom;

        // Calculate x position for each measurement
        const getXPosition = (index) => {
            if (timelineMeasurements.length === 1) return padding.left + graphWidth / 2;
            return padding.left + (index / (timelineMeasurements.length - 1)) * graphWidth;
        };

        // Calculate y position for a value
        const getYPosition = (value) => {
            if (value === undefined || value === null) return null;
            const normalized = (value - minValue) / valueRange;
            return padding.top + graphHeight - (normalized * graphHeight);
        };

        // Generate path for a field
        const generatePath = (fields, fieldIndex, colorPalette = colors) => {
            const field = fields[fieldIndex];
            const points = [];

            timelineMeasurements.forEach((m, index) => {
                const value = m.measurements?.[field];
                const y = getYPosition(value);
                if (y !== null) {
                    points.push({ x: getXPosition(index), y, value, index });
                }
            });

            if (points.length < 2) return null;

            const pathD = points.map((p, i) =>
                `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
            ).join(' ');

            return { pathD, points, color: colorPalette[fieldIndex], field };
        };

        // Render a single chart
        const renderChart = (fields, title, colorPalette = colors) => {
            const paths = fields.map((_, i) => generatePath(fields, i, colorPalette)).filter(p => p !== null);

            return (
                <div className="flex-1 min-w-0">
                    <h4 className="text-center font-semibold text-gray-700 mb-2">{title}</h4>
                    <div className="overflow-x-auto">
                        <svg width={svgWidth} height={svgHeight} className="bg-white border border-gray-200 rounded">
                            {/* Grid lines */}
                            {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                                const y = padding.top + graphHeight * (1 - ratio);
                                const value = Math.round(minValue + valueRange * ratio);
                                return (
                                    <g key={i}>
                                        <line
                                            x1={padding.left}
                                            y1={y}
                                            x2={padding.left + graphWidth}
                                            y2={y}
                                            stroke="#E5E7EB"
                                            strokeDasharray="4,4"
                                        />
                                        <text
                                            x={padding.left - 10}
                                            y={y + 4}
                                            textAnchor="end"
                                            className="text-xs fill-gray-500"
                                        >
                                            {value}
                                        </text>
                                    </g>
                                );
                            })}

                            {/* X-axis labels (dates) */}
                            {timelineMeasurements.map((m, index) => {
                                const x = getXPosition(index);
                                const date = new Date(m.createdAt);
                                const dateStr = date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: '2-digit' });
                                return (
                                    <g key={index}>
                                        <line
                                            x1={x}
                                            y1={padding.top}
                                            x2={x}
                                            y2={padding.top + graphHeight}
                                            stroke="#E5E7EB"
                                            strokeDasharray="2,2"
                                        />
                                        <text
                                            x={x}
                                            y={svgHeight - 10}
                                            textAnchor="middle"
                                            className="text-xs fill-gray-500"
                                            transform={`rotate(-45, ${x}, ${svgHeight - 10})`}
                                        >
                                            {dateStr}
                                        </text>
                                    </g>
                                );
                            })}

                            {/* Lines and points */}
                            {paths.map((pathData, pathIndex) => (
                                <g key={pathIndex}>
                                    {/* Line */}
                                    <path
                                        d={pathData.pathD}
                                        fill="none"
                                        stroke={pathData.color}
                                        strokeWidth="2"
                                    />
                                    {/* Points */}
                                    {pathData.points.map((point, pointIndex) => (
                                        <g key={pointIndex}>
                                            <circle
                                                cx={point.x}
                                                cy={point.y}
                                                r="4"
                                                fill={pathData.color}
                                            />
                                            {/* Value tooltip on hover */}
                                            <title>{`${fieldLabels[pathData.field]}: ${point.value} cm`}</title>
                                        </g>
                                    ))}
                                </g>
                            ))}
                        </svg>
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap justify-center gap-3 mt-3">
                        {fields.map((field, i) => (
                            <div key={field} className="flex items-center gap-1">
                                <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: colorPalette[i] }}
                                />
                                <span className="text-xs text-gray-600">{fieldLabels[field]}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        };

        // Render measurements table
        const renderMeasurementsTable = (fields, title, colorPalette = colors) => (
            <div className="flex-1 min-w-0">
                <h4 className="text-center font-semibold text-gray-700 mb-2">{title}</h4>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border border-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-2 py-1 border-r border-gray-200 text-left text-xs font-medium text-gray-500">{t('patients.evolution.field')}</th>
                                {timelineMeasurements.map((m, i) => (
                                    <th key={i} className="px-2 py-1 border-r border-gray-200 text-center text-xs font-medium text-gray-500">
                                        {new Date(m.createdAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: '2-digit' })}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {fields.map((field, fieldIndex) => (
                                <tr key={field} className={fieldIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-2 py-1 border-r border-gray-200 font-medium" style={{ color: colorPalette[fieldIndex] }}>
                                        {fieldLabels[field]}
                                    </td>
                                    {timelineMeasurements.map((m, mIndex) => {
                                        const currentValue = m.measurements?.[field];
                                        const prevValue = mIndex > 0 ? timelineMeasurements[mIndex - 1].measurements?.[field] : null;
                                        const diff = currentValue !== undefined && prevValue !== undefined ? currentValue - prevValue : null;

                                        return (
                                            <td key={mIndex} className="px-2 py-1 border-r border-gray-200 text-center">
                                                <div className="flex flex-col items-center">
                                                    <span className="font-medium">{currentValue ?? '-'}</span>
                                                    {mIndex > 0 && diff !== null && diff !== 0 && (
                                                        <span className={`text-xs ${diff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                            {diff > 0 ? '+' : ''}{diff.toFixed(1)}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] flex flex-col">
                    {/* Dialog Header */}
                    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">{t('patients.evolution.title')}</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {t('patients.evolution.patient')}: {evolutionPatientName} ({evolutionPatientId})
                            </p>
                        </div>
                        <button
                            onClick={() => setShowEvolutionDialog(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Dialog Content */}
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        {dialogLoading ? (
                            <div className="py-12">
                                <LoadingSpinner size="md" text={t('patients.evolution.loading')} />
                            </div>
                        ) : timelineMeasurements.length === 0 ? (
                            <div className="text-center py-12">
                                <p className="text-gray-500">{t('patients.evolution.noMeasurements')}</p>
                            </div>
                        ) : timelineMeasurements.length === 1 ? (
                            <div className="text-center py-12">
                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                <p className="mt-4 text-gray-500">{t('patients.evolution.singleMeasurement')}</p>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {/* Line Charts */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('patients.evolution.circumferenceEvolution')}</h3>
                                    {/* Hip measurements chart */}
                                    {timelineMeasurements.some(m => m.measurements?.cT || m.measurements?.cH) && (
                                        <div className="mb-6">
                                            <div className="max-w-2xl mx-auto">
                                                {renderChart(hipFields, t('patients.evolution.hipMeasurements'), hipColors)}
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {renderChart(rightLegFields, t('patients.evolution.rightLeg'), colors)}
                                        {renderChart(leftLegFields, t('patients.evolution.leftLeg'), colors)}
                                    </div>
                                </div>

                                {/* Data Tables */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('patients.evolution.measurementValues')}</h3>
                                    {/* Hip measurements table */}
                                    {timelineMeasurements.some(m => m.measurements?.cT || m.measurements?.cH) && (
                                        <div className="mb-6 max-w-2xl mx-auto">
                                            {renderMeasurementsTable(hipFields, t('patients.evolution.hipMeasurements'), hipColors)}
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {renderMeasurementsTable(rightLegFields, t('patients.evolution.rightLeg'), colors)}
                                        {renderMeasurementsTable(leftLegFields, t('patients.evolution.leftLeg'), colors)}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Dialog Footer */}
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                        <p className="text-sm text-gray-600">
                            {t('patients.evolution.countInTimeline', { count: timelineMeasurements.length, plural: timelineMeasurements.length !== 1 ? 's' : '' })}
                        </p>
                        <button
                            onClick={() => setShowEvolutionDialog(false)}
                            className="btn-secondary"
                        >
                            {t('patients.evolution.close')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const MeasurementHistoryDialog = () => {
        if (!showMeasurementDialog) return null;

        const sortedMeasurements = getSortedMeasurements();

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                    {/* Dialog Header */}
                    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">{t('patients.measurementHistory.title')}</h2>
                            {sortedMeasurements.length > 0 && (
                                <p className="text-sm text-gray-600 mt-1">
                                    {t('patients.measurementHistory.patient')}:{' '}
                                    {formatPatientNameForDisplay(
                                        sortedMeasurements[0].patientFirstName,
                                        sortedMeasurements[0].patientLastName,
                                        sortedMeasurements[0].patientName
                                    )}{' '}
                                    ({sortedMeasurements[0].patientId})
                                </p>
                            )}
                        </div>
                        <button
                            onClick={() => setShowMeasurementDialog(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Dialog Content */}
                    <div ref={measurementDialogScrollRef} className="flex-1 overflow-y-auto px-6 py-4">
                        {dialogLoading ? (
                            <div className="py-12">
                                <LoadingSpinner size="md" text={t('patients.measurementHistory.loading')} />
                            </div>
                        ) : sortedMeasurements.length === 0 ? (
                            <div className="text-center py-12">
                                <p className="text-gray-500">{t('patients.measurementHistory.noMeasurements')}</p>
                            </div>
                        ) : (
                            <>
                                {/* Desktop/Tablet Table View - Hidden on mobile */}
                                <div className="hidden md:block overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedMeasurementsToDelete.length === sortedMeasurements.length && sortedMeasurements.length > 0}
                                                        onChange={handleToggleAllMeasurements}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                </th>
                                                <th
                                                    onClick={() => setMeasurementSortOrder(measurementSortOrder === 'asc' ? 'desc' : 'asc')}
                                                    className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                >
                                                    <div className="flex items-center">
                                                        {t('patients.measurementHistory.headers.date')}
                                                        {measurementSortOrder === 'asc' ? (
                                                            <svg className="w-4 h-4 ml-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4 ml-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t('patients.measurementHistory.headers.vendor')} / {t('patients.measurementHistory.headers.model')}
                                                </th>
                                                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t('patients.measurementHistory.headers.compressionLength')}
                                                </th>
                                                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t('patients.measurementHistory.headers.ordered')}
                                                </th>
                                                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t('patients.measurementHistory.headers.closed')}
                                                </th>
                                                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t('patients.measurementHistory.headers.actions')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {sortedMeasurements.map((measurement, index) => (
                                                <tr
                                                    key={measurement._id}
                                                    className={`hover:bg-gray-50 transition-colors ${selectedMeasurementsToDelete.includes(measurement._id) ? 'bg-blue-50' : ''}`}
                                                >
                                                    <td className="px-2 py-2 whitespace-nowrap">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedMeasurementsToDelete.includes(measurement._id)}
                                                            onChange={() => handleToggleMeasurementSelection(measurement._id)}
                                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900">
                                                        <div className="font-medium text-gray-900 leading-tight">
                                                            {formatDateWithDay(measurement.createdAt)}
                                                        </div>
                                                        <div className="mt-0.5 text-xs text-gray-500 leading-tight">
                                                            {t('patients.measurementHistory.measuredByShort')}: {formatMeasuredByName(measurement)}
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900">
                                                        <div className="flex items-center gap-1.5">
                                                            <span>{measurement.selectedProduct?.vendorName || <span className="text-gray-400">{t('patients.measurementHistory.notSelected')}</span>}</span>
                                                            {measurement.measurementType === 'madeToMeasure' && (
                                                                <span className="relative group inline-flex items-center cursor-default">
                                                                    <svg className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                                        <path d="M15.98 1.804a1 1 0 00-1.96 0l-.24 1.192a1 1 0 01-.784.785l-1.192.238a1 1 0 000 1.962l1.192.238a1 1 0 01.785.785l.238 1.192a1 1 0 001.962 0l.238-1.192a1 1 0 01.785-.785l1.192-.238a1 1 0 000-1.962l-1.192-.238a1 1 0 01-.785-.785l-.238-1.192zM6.949 5.684a1 1 0 00-1.898 0l-.683 2.051a1 1 0 01-.633.633l-2.051.683a1 1 0 000 1.898l2.051.684a1 1 0 01.633.632l.683 2.051a1 1 0 001.898 0l.683-2.051a1 1 0 01.633-.633l2.051-.683a1 1 0 000-1.897l-2.051-.684a1 1 0 01-.633-.633L6.95 5.684zM13.949 13.684a1 1 0 00-1.898 0l-.184.551a1 1 0 01-.632.633l-.551.183a1 1 0 000 1.898l.551.183a1 1 0 01.633.633l.183.551a1 1 0 001.898 0l.184-.551a1 1 0 01.632-.633l.551-.183a1 1 0 000-1.898l-.551-.184a1 1 0 01-.633-.632l-.183-.551z" />
                                                                    </svg>
                                                                    <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-0.5 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                                                                        {t('patients.measurementHistory.madeToMeasure')}
                                                                    </span>
                                                                </span>
                                                            )}
                                                        </div>
                                                        {measurement.selectedProduct?.modelName && (
                                                            <div className="mt-0.5 text-xs text-gray-500">
                                                                {measurement.selectedProduct.modelName}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500">
                                                        {measurement.compressionClass && measurement.lengthType ? (
                                                            `${measurement.compressionClass} / ${measurement.lengthType}`
                                                        ) : (
                                                            <span className="text-gray-400">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={measurement.ordered || false}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleOrdered(measurement._id, e.target.checked);
                                                            }}
                                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                            title={measurement.orderedDate ? t('patients.measurementHistory.orderedOn', { date: formatDate(measurement.orderedDate) }) : t('patients.measurementHistory.markOrdered')}
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={measurement.closed || false}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleClosed(measurement._id, e.target.checked);
                                                            }}
                                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                            title={t('patients.measurementHistory.markClosed')}
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2 whitespace-nowrap text-center text-sm">
                                                        <div className="flex items-center justify-center gap-1">
                                                            <button
                                                                onClick={() => handleSelectMeasurement(measurement, !measurement.ordered)}
                                                                className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors inline-flex items-center justify-center"
                                                                title={measurement.ordered ? t('patients.measurementHistory.viewDetails') : (t('common.edit') || 'Edit')}
                                                            >
                                                                {measurement.ordered ? (
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                    </svg>
                                                                ) : (
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" />
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                                    </svg>
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => handleDownloadInternalPdf(measurement)}
                                                                className="p-2 text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded-lg transition-colors inline-flex items-center justify-center"
                                                                title={t('patients.measurementHistory.downloadInternalPdf')}
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                                                </svg>
                                                            </button>
                                                            {!measurement.ordered && (
                                                                <button
                                                                    onClick={() => handleCreateOrder(measurement)}
                                                                    className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors inline-flex items-center justify-center"
                                                                    title={t('patients.measurementHistory.createOrder')}
                                                                >
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                                                    </svg>
                                                                </button>
                                                            )}
                                                            {measurement.ordered && (
                                                                <button
                                                                    onClick={() => handleDownloadOrderPdf(measurement)}
                                                                    className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors inline-flex items-center justify-center"
                                                                    title={t('patients.measurementHistory.downloadOrderPdf')}
                                                                >
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                    </svg>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Card View - Visible only on mobile */}
                                <div className="md:hidden space-y-4">
                                    {/* Sort button for mobile */}
                                    <div className="flex justify-between items-center pb-2">
                                        <button
                                            onClick={() => setMeasurementSortOrder(measurementSortOrder === 'asc' ? 'desc' : 'asc')}
                                            className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={measurementSortOrder === 'asc' ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                                            </svg>
                                            {t('patients.measurementHistory.sortByDate')} ({measurementSortOrder === 'asc' ? t('patients.measurementHistory.oldest') : t('patients.measurementHistory.newest')})
                                        </button>
                                        {sortedMeasurements.length > 0 && (
                                            <label className="flex items-center gap-2 text-sm text-gray-600">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedMeasurementsToDelete.length === sortedMeasurements.length}
                                                    onChange={handleToggleAllMeasurements}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                {t('patients.measurementHistory.selectAll')}
                                            </label>
                                        )}
                                    </div>

                                    {sortedMeasurements.map((measurement) => (
                                        <div
                                            key={measurement._id}
                                            className={`bg-white rounded-lg shadow border transition-all ${selectedMeasurementsToDelete.includes(measurement._id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                                                } p-4`}
                                        >
                                            {/* Header with checkbox and date */}
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-start gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedMeasurementsToDelete.includes(measurement._id)}
                                                        onChange={() => handleToggleMeasurementSelection(measurement._id)}
                                                        className="w-5 h-5 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <div>
                                                        <div className="font-semibold text-gray-900">
                                                            {formatDateWithDay(measurement.createdAt)}
                                                        </div>
                                                        <div className="mt-1 text-xs text-gray-500">
                                                            {t('patients.measurementHistory.measuredByShort')}: {formatMeasuredByName(measurement)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Product info */}
                                            <div className="mb-3 space-y-2">
                                                <div>
                                                    <div className="text-xs font-medium text-gray-500 mb-1">{t('patients.measurementHistory.headers.vendor')}</div>
                                                    <div className="flex items-center gap-1.5 text-sm text-gray-900">
                                                        <span>{measurement.selectedProduct?.vendorName || <span className="text-gray-400">{t('patients.measurementHistory.notSelected')}</span>}</span>
                                                        {measurement.measurementType === 'madeToMeasure' && (
                                                            <span className="relative group inline-flex items-center cursor-default">
                                                                <svg className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path d="M15.98 1.804a1 1 0 00-1.96 0l-.24 1.192a1 1 0 01-.784.785l-1.192.238a1 1 0 000 1.962l1.192.238a1 1 0 01.785.785l.238 1.192a1 1 0 001.962 0l.238-1.192a1 1 0 01.785-.785l1.192-.238a1 1 0 000-1.962l-1.192-.238a1 1 0 01-.785-.785l-.238-1.192zM6.949 5.684a1 1 0 00-1.898 0l-.683 2.051a1 1 0 01-.633.633l-2.051.683a1 1 0 000 1.898l2.051.684a1 1 0 01.633.632l.683 2.051a1 1 0 001.898 0l.683-2.051a1 1 0 01.633-.633l2.051-.683a1 1 0 000-1.897l-2.051-.684a1 1 0 01-.633-.633L6.95 5.684zM13.949 13.684a1 1 0 00-1.898 0l-.184.551a1 1 0 01-.632.633l-.551.183a1 1 0 000 1.898l.551.183a1 1 0 01.633.633l.183.551a1 1 0 001.898 0l.184-.551a1 1 0 01.632-.633l.551-.183a1 1 0 000-1.898l-.551-.184a1 1 0 01-.633-.632l-.183-.551z" />
                                                                </svg>
                                                                <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-0.5 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                                                                    {t('patients.measurementHistory.madeToMeasure')}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {measurement.selectedProduct?.modelName && (
                                                    <div>
                                                        <div className="text-xs font-medium text-gray-500 mb-1">{t('patients.measurementHistory.headers.model')}</div>
                                                        <div className="text-sm text-gray-900">{measurement.selectedProduct.modelName}</div>
                                                    </div>
                                                )}
                                                {measurement.compressionClass && measurement.lengthType && (
                                                    <div>
                                                        <div className="text-xs font-medium text-gray-500 mb-1">{t('patients.measurementHistory.headers.compressionLength')}</div>
                                                        <div className="text-sm text-gray-500">
                                                            {measurement.compressionClass} / {measurement.lengthType}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Status checkboxes */}
                                            <div className="flex gap-4 mb-3 pb-3 border-b border-gray-200">
                                                <label className="flex items-center gap-2 text-sm">
                                                    <input
                                                        type="checkbox"
                                                        checked={measurement.ordered || false}
                                                        onChange={(e) => handleToggleOrdered(measurement._id, e.target.checked)}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className="text-gray-700">{t('patients.measurementHistory.headers.ordered')}</span>
                                                </label>
                                                <label className="flex items-center gap-2 text-sm">
                                                    <input
                                                        type="checkbox"
                                                        checked={measurement.closed || false}
                                                        onChange={(e) => handleToggleClosed(measurement._id, e.target.checked)}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className="text-gray-700">{t('patients.measurementHistory.headers.closed')}</span>
                                                </label>
                                            </div>

                                            {/* Action buttons */}
                                            <div className="grid gap-2 grid-cols-3">
                                                <button
                                                    onClick={() => handleSelectMeasurement(measurement, !measurement.ordered)}
                                                    className="flex items-center justify-center gap-2 px-3 py-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors font-medium text-sm border border-blue-200"
                                                >
                                                    {measurement.ordered ? (
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                        </svg>
                                                    )}
                                                    <span className="hidden sm:inline">{measurement.ordered ? t('patients.measurementHistory.view') : (t('common.edit') || 'Edit')}</span>
                                                </button>
                                                <button
                                                    onClick={() => handleDownloadInternalPdf(measurement)}
                                                    className="flex items-center justify-center gap-2 px-3 py-2 text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded-lg transition-colors font-medium text-sm border border-purple-200"
                                                    title={t('patients.measurementHistory.downloadInternalPdf')}
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                                    </svg>
                                                    <span className="hidden sm:inline">{t('patients.measurementHistory.internalPdf')}</span>
                                                </button>
                                                {!measurement.ordered && (
                                                    <button
                                                        onClick={() => handleCreateOrder(measurement)}
                                                        className="flex items-center justify-center gap-2 px-3 py-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors font-medium text-sm border border-green-200"
                                                        title={t('patients.measurementHistory.createOrder')}
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                                        </svg>
                                                        <span className="hidden sm:inline">{t('patients.measurementHistory.createOrder')}</span>
                                                    </button>
                                                )}
                                                {measurement.ordered && (
                                                    <button
                                                        onClick={() => handleDownloadOrderPdf(measurement)}
                                                        className="flex items-center justify-center gap-2 px-3 py-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors font-medium text-sm border border-red-200"
                                                        title={t('patients.measurementHistory.downloadOrderPdf')}
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        <span className="hidden sm:inline">{t('patients.measurementHistory.orderPdf')}</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Dialog Footer */}
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                        <div>
                            <p className="text-sm text-gray-600">
                                {t('patients.measurementHistory.countFound', { count: sortedMeasurements.length, plural: sortedMeasurements.length !== 1 ? 's' : '' })}
                            </p>
                            {selectedMeasurementsToDelete.length > 0 && (
                                <p className="text-sm text-blue-600 mt-1">
                                    {t('patients.measurementHistory.countSelected', { count: selectedMeasurementsToDelete.length, plural: selectedMeasurementsToDelete.length !== 1 ? 's' : '' })}
                                </p>
                            )}
                        </div>
                        <div className="flex gap-2">
                            {selectedMeasurementsToDelete.length > 0 && (
                                <button
                                    onClick={handleDeleteMeasurements}
                                    disabled={isDeleting}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                                >
                                    {isDeleting ? (
                                        <>
                                            <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            {t('patients.measurementHistory.deleting')}
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            {t('patients.measurementHistory.deleteSelected')}
                                        </>
                                    )}
                                </button>
                            )}
                            <button
                                onClick={() => setShowMeasurementDialog(false)}
                                className="btn-secondary"
                            >
                                {t('patients.measurementHistory.close')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Early returns
    if (loading) {
        return (
            <div className="page-transition min-h-screen bg-gray-50 py-8">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="py-12">
                        <LoadingSpinner size="lg" text={t('patients.loading')} />
                    </div>
                </div>
            </div>
        );
    }

    // Show upgrade screen when subscription has expired
    if (!isSubscriptionActive()) {
        const isFreeTrial = user?.subscription === 'FREE-TRIAL';
        const expiryDate = user?.subscriptionExpiry
            ? new Date(user.subscriptionExpiry).toLocaleDateString()
            : null;

        return (
            <div className="page-transition min-h-screen bg-gray-50 py-8">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold">{t('patients.title')}</h1>
                        <p className="text-gray-600 mt-1">{t('patients.pageSubtitle')}</p>
                    </div>

                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="card max-w-lg w-full text-center border-2 border-red-200 bg-red-50">
                            {/* Lock icon */}
                            <div className="flex justify-center mb-6">
                                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                                    <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                            </div>

                            <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                {isFreeTrial ? t('profile.subscription.trialExpired') : t('profile.subscription.planExpired', { plan: user?.subscription })}
                            </h2>

                            {expiryDate && (
                                <p className="text-sm text-red-600 font-medium mb-4">
                                    {isFreeTrial
                                        ? t('patients.subscription.trialExpiredOn', { date: expiryDate })
                                        : t('patients.subscription.planExpiredOn', { plan: user?.subscription, date: expiryDate })}
                                </p>
                            )}

                            {isFreeTrial ? (
                                <p className="text-gray-600 mb-6">
                                    {t('patients.subscription.trialMessage')}
                                </p>
                            ) : (
                                <p className="text-gray-600 mb-6">
                                    {t('patients.subscription.planMessage', { plan: user?.subscription })}
                                </p>
                            )}

                            <div className="bg-white border border-red-200 rounded-lg p-4 mb-6 text-left">
                                {isFreeTrial ? (
                                    <>
                                        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('patients.subscription.whatsIncluded')}</h3>
                                        <ul className="space-y-2 text-sm text-gray-600">
                                            <li className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                {t('patients.subscription.features.fullAccess')}
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                {t('patients.subscription.features.offTheShelf')}
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span>{t('patients.subscription.features.madeToMeasure')} <span className="text-purple-600 font-medium">({t('patients.subscription.features.proOnly')})</span></span>
                                            </li>
                                        </ul>
                                    </>
                                ) : (
                                    <>
                                        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('patients.subscription.renewingRestores')}</h3>
                                        <ul className="space-y-2 text-sm text-gray-600">
                                            <li className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                {t('patients.subscription.features.fullAccess')}
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                {t('patients.subscription.features.offTheShelf')}
                                            </li>
                                            {user?.subscription === 'PRO' && (
                                                <li className="flex items-center gap-2">
                                                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    <span>{t('patients.subscription.features.madeToMeasure')} <span className="text-purple-600 font-medium">({t('patients.subscription.features.pro')})</span></span>
                                                </li>
                                            )}
                                        </ul>
                                    </>
                                )}
                            </div>

                            <Link
                                to="/organization-settings"
                                state={{ tab: 'subscription' }}
                                className="btn-primary inline-block w-full text-center"
                            >
                                {isFreeTrial ? t('profile.subscription.upgradeNow') : t('profile.subscription.payNow')}
                            </Link>

                            <p className="text-xs text-gray-500 mt-4">
                                {t('patients.subscription.dataPreserved', { action: t(isFreeTrial ? 'patients.subscription.upgrading' : 'patients.subscription.renewing') })}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-transition min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4 max-w-7xl">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-3xl font-bold">{t('patients.title')}</h1>
                        <button
                            onClick={() => navigate('/patient-info')}
                            className="btn-primary flex items-center"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t('patients.newPatient')}
                        </button>
                    </div>

                    <p className="text-gray-600">
                        {t('patients.pageSubtitle')}
                    </p>
                </div>

                {/* Search and Filter Controls */}
                <div className="card mb-6">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
                        {/* Search */}
                        <div className="lg:col-span-7">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patients.search.label')}
                            </label>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        placeholder={t('patients.search.placeholder')}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleApplySearch();
                                            }
                                        }}
                                        className="h-10 w-full pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    <svg
                                        className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleApplySearch}
                                    className="btn-secondary h-10 px-4 whitespace-nowrap"
                                >
                                    {t('common.search', { defaultValue: 'Search' })}
                                </button>
                            </div>
                        </div>

                        {/* Measured By Filter */}
                        <div className="lg:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('patients.search.filterByMeasuredBy')}
                            </label>
                            <select
                                value={measuredByFilter}
                                onChange={(e) => setMeasuredByFilter(e.target.value)}
                                className="h-10 w-full px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t('patients.search.allUsers')}</option>
                                {measuredByOptions.map((name) => (
                                    <option key={name} value={name}>
                                        {name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Clear Filters */}
                        <div className="lg:col-span-2">
                            <label className="block text-sm font-medium text-transparent mb-2 select-none">.</label>
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setAppliedSearchTerm('');
                                    setMeasuredByFilter('');
                                    setCurrentPage(1);
                                }}
                                disabled={!searchTerm && !measuredByFilter}
                                className="btn-secondary h-10 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t('patients.search.clearFilters')}
                            </button>
                        </div>
                    </div>

                    {/* Results count */}
                    <div className="mt-4 text-sm text-gray-600">
                        {t('patients.search.showing', { count: patients.length, total: totalPatients })}
                    </div>
                </div>

                {/* Patients Grid */}
                {totalPatients === 0 ? (
                    <div className="card text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('patients.noPatients')}</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {appliedSearchTerm ? t('patients.empty.searchHint') : t('patients.empty.createHint')}
                        </p>
                        {!appliedSearchTerm && (
                            <div className="mt-6">
                                <button
                                    onClick={() => navigate('/patient-info')}
                                    className="btn-primary"
                                >
                                    {t('patients.empty.addButton')}
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Desktop/Tablet Table View - Hidden on mobile */}
                        <div className="hidden md:block card overflow-visible">
                            <div className="overflow-x-auto overflow-y-visible min-h-[12.5rem]">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th
                                                onClick={() => handleSort('patientName')}
                                                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            >
                                                <div className="flex items-center">
                                                    {t('patients.tableHeaders.patientName')}
                                                    <SortIcon field="patientName" />
                                                </div>
                                            </th>
                                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {t('patients.tableHeaders.measurements')}
                                            </th>
                                            <th
                                                onClick={() => handleSort('updatedAt')}
                                                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            >
                                                <div className="flex items-center">
                                                    {t('patients.tableHeaders.lastUpdated')}
                                                    <SortIcon field="updatedAt" />
                                                </div>
                                            </th>
                                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {t('patients.tableHeaders.latestProduct')}
                                            </th>
                                            <th
                                                onClick={() => handleSort('measuredBy')}
                                                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            >
                                                <div className="flex items-center">
                                                    {t('patients.tableHeaders.measuredBy')}
                                                    <SortIcon field="measuredBy" />
                                                </div>
                                            </th>
                                            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {t('patients.tableHeaders.completed')}
                                            </th>
                                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {t('patients.tableHeaders.actions')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {patients.map((patient, index) => (
                                            <tr
                                                key={patient.patientId}
                                                className="hover:bg-gray-50 cursor-pointer transition-colors"
                                                onClick={() => handlePatientClick(patient)}
                                            >
                                                <td className="px-3 py-3 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                            <span className="text-blue-600 font-semibold text-xs">
                                                                {patient.patientLastName ? patient.patientLastName.charAt(0).toUpperCase() : '?'}
                                                            </span>
                                                        </div>
                                                        <div className="ml-2">
                                                            <div className="text-sm font-medium text-gray-900 leading-tight">
                                                                {patient.patientLastName || t('patients.unknownPatient', { defaultValue: 'Patient' })}
                                                            </div>
                                                            {patient.patientFirstName && (
                                                                <div className="text-xs text-gray-500 leading-tight">
                                                                    {patient.patientFirstName}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-1">
                                                        <span
                                                            onClick={(e) => handleViewLatestMeasurement(patient, e)}
                                                            className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200 transition-colors"
                                                        >
                                                            {t('patients.records', { count: patient.measurementCount, plural: patient.measurementCount !== 1 ? 's' : '' })}
                                                        </span>
                                                        <button
                                                            onClick={(e) => handleShowEvolution(patient, e)}
                                                            className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                            title={t('patients.tooltips.viewEvolution')}
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="leading-tight">{formatDateParts(patient.updatedAt).datePart}</div>
                                                    <div className="text-xs text-gray-400 leading-tight">{formatDateParts(patient.updatedAt).timePart}</div>
                                                </td>
                                                <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                                                    {patient.latestMeasurement.selectedProduct ? (
                                                        <div
                                                            onClick={(e) => handleViewLatestProduct(patient, e)}
                                                            className="cursor-pointer hover:bg-blue-50 p-1 rounded transition-colors"
                                                        >
                                                            <div className="font-medium text-blue-600 hover:text-blue-800 text-xs">
                                                                {patient.latestMeasurement.selectedProduct.vendorName}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {patient.latestMeasurement.selectedProduct.modelName}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs">{t('patients.noProductSelected')}</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900" title={patient.measuredBy}>
                                                    {formatMeasuredByCompact(patient.measuredBy)}
                                                </td>
                                                <td className="px-2 py-3 whitespace-nowrap text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={isPatientCompleted(patient)}
                                                        readOnly
                                                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-default"
                                                        title={isPatientCompleted(patient) ? t('patients.tooltips.allCompleted') : t('patients.tooltips.somePending')}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </td>
                                                <td className={`px-3 py-3 whitespace-nowrap text-right text-sm font-medium ${activeActionsMenuId === patient.patientId ? 'relative z-30' : ''}`}>
                                                    <div
                                                        className="relative inline-block text-left patient-actions-menu"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                toggleActionsMenu(patient.patientId, index, e);
                                                            }}
                                                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                                            title={t('patients.tableHeaders.actions')}
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h.01M12 12h.01M18 12h.01" />
                                                            </svg>
                                                        </button>

                                                        {activeActionsMenuId === patient.patientId && (
                                                            <div className={`absolute right-0 z-50 w-56 rounded-lg border border-gray-200 bg-white shadow-lg ${actionsMenuDirection === 'up' ? 'bottom-full mb-2 origin-bottom-right' : 'mt-2 origin-top-right'}`}>
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        handleNewMeasurement(patient, e);
                                                                        setActiveActionsMenuId(null);
                                                                    }}
                                                                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                                >
                                                                    <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                                    </svg>
                                                                    {t('patients.newMeasurement')}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => handleOrderAgain(patient, e)}
                                                                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                                >
                                                                    <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h14M9 6l-4 4 4 4m3-7h7v11H5v-4" />
                                                                    </svg>
                                                                    {t('patients.actions.orderAgain', { defaultValue: 'Order same again' })}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => handleSwapPatientNames(patient, e)}
                                                                    disabled={swappingPatientId === patient.patientId}
                                                                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                                >
                                                                    {swappingPatientId === patient.patientId ? (
                                                                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-600"></div>
                                                                    ) : (
                                                                        <svg className="h-4 w-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                                                        </svg>
                                                                    )}
                                                                    {t('patientInfo.buttons.swapNames') || 'Swap First/Last Names'}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Mobile Card View - Visible only on mobile */}
                        <div className="md:hidden space-y-4">
                            {patients.map((patient, index) => (
                                <div
                                    key={patient.patientId}
                                    className="card hover:shadow-lg cursor-pointer transition-shadow"
                                    onClick={() => handlePatientClick(patient)}
                                >
                                    {/* Patient Header */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center flex-1">
                                            <div className="flex-shrink-0 h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-blue-600 font-semibold text-lg">
                                                    {patient.patientLastName ? patient.patientLastName.charAt(0).toUpperCase() : '?'}
                                                </span>
                                            </div>
                                            <div className="ml-3 flex-1 min-w-0">
                                                <h3 className="text-base font-semibold text-gray-900 truncate">
                                                    {formatPatientNameForDisplay(patient.patientFirstName, patient.patientLastName)}
                                                </h3>
                                                <p className="text-xs text-gray-500">
                                                    {formatDate(patient.updatedAt)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center ml-2">
                                            <input
                                                type="checkbox"
                                                checked={isPatientCompleted(patient)}
                                                readOnly
                                                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 cursor-default"
                                                title={isPatientCompleted(patient) ? t('patients.tooltips.allCompleted') : t('patients.tooltips.somePending')}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>

                                    {/* Measurements and Evolution */}
                                    <div className="flex items-center gap-2 mb-3">
                                        <span
                                            onClick={(e) => handleViewLatestMeasurement(patient, e)}
                                            className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200 transition-colors"
                                        >
                                            {t('patients.records', { count: patient.measurementCount, plural: patient.measurementCount !== 1 ? 's' : '' })}
                                        </span>
                                        <button
                                            onClick={(e) => handleShowEvolution(patient, e)}
                                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title={t('patients.tooltips.viewEvolution')}>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Latest Product */}
                                    <div className="mb-3">
                                        <div className="text-xs font-medium text-gray-500 mb-1">{t('patients.tableHeaders.latestProduct')}</div>
                                        {patient.latestMeasurement.selectedProduct ? (
                                            <div
                                                onClick={(e) => handleViewLatestProduct(patient, e)}
                                                className="cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors -mx-2"
                                            >
                                                <div className="font-medium text-blue-600 hover:text-blue-800 text-sm">
                                                    {patient.latestMeasurement.selectedProduct.vendorName}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {patient.latestMeasurement.selectedProduct.modelName}
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-sm text-gray-400">{t('patients.noProductSelected')}</span>
                                        )}
                                    </div>

                                    {/* Measured By */}
                                    <div className="mb-3">
                                        <div className="text-xs font-medium text-gray-500 mb-1">{t('patients.tableHeaders.measuredBy')}</div>
                                        <div className="text-sm text-gray-900">{patient.measuredBy}</div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex justify-end pt-3 border-t border-gray-200">
                                        <div
                                            className="relative inline-block text-left patient-actions-menu"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    toggleActionsMenu(patient.patientId, index, e);
                                                }}
                                                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors font-medium text-sm"
                                                title={t('patients.tableHeaders.actions')}
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h.01M12 12h.01M18 12h.01" />
                                                </svg>
                                            </button>

                                            {activeActionsMenuId === patient.patientId && (
                                                <div className={`absolute right-0 z-50 w-56 rounded-lg border border-gray-200 bg-white shadow-lg ${actionsMenuDirection === 'up' ? 'bottom-full mb-2 origin-bottom-right' : 'mt-2 origin-top-right'}`}>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            handleNewMeasurement(patient, e);
                                                            setActiveActionsMenuId(null);
                                                        }}
                                                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                    >
                                                        <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        {t('patients.newMeasurement')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleOrderAgain(patient, e)}
                                                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                    >
                                                        <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h14M9 6l-4 4 4 4m3-7h7v11H5v-4" />
                                                        </svg>
                                                        {t('patients.actions.orderAgain', { defaultValue: 'Order same again' })}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleSwapPatientNames(patient, e)}
                                                        disabled={swappingPatientId === patient.patientId}
                                                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        {swappingPatientId === patient.patientId ? (
                                                            <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-600"></div>
                                                        ) : (
                                                            <svg className="h-4 w-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                                            </svg>
                                                        )}
                                                        {t('patientInfo.buttons.swapNames') || 'Swap First/Last Names'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {totalPatients > PATIENTS_PER_PAGE && (
                            <div className="mt-6 flex items-center justify-between gap-4">
                                <button
                                    type="button"
                                    onClick={goToPreviousPage}
                                    disabled={currentPage === 1}
                                    className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {t('common.previous', { defaultValue: 'Previous' })}
                                </button>

                                <div className="text-sm text-gray-600">
                                    {t('patients.pagination.page', {
                                        defaultValue: 'Page {{current}} of {{total}}',
                                        current: currentPage,
                                        total: totalPages,
                                    })}
                                </div>

                                <button
                                    type="button"
                                    onClick={goToNextPage}
                                    disabled={currentPage === totalPages}
                                    className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {t('common.next', { defaultValue: 'Next' })}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Measurement History Dialog */}
            <MeasurementHistoryDialog />

            {/* Measurements Evolution Dialog */}
            <MeasurementsEvolutionDialog />

            {/* Alert Messages */}
            <Alert type="error" message={error} onDismiss={() => setError('')} />
            <Alert type="success" message={success} onDismiss={() => setSuccess('')} />

            {/* Confirmation Dialog */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
            />
        </div>
    );
};

export default Patients;
